import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import type { RefObject } from "react"
import { DateTime } from "luxon"

import type { DateTimeValidationError } from "@mui/x-date-pickers"

import type { EntityType } from "types/common.types"
import { FORECAST_MAX_DAYS } from "helpers/utils/constants"
import { hasForecast } from "helpers/utils/dateUtils"

/**
 * Entity driven constraints for the date fields. Powerlines and spans have a
 * forecast, so they accept dates up to FORECAST_MAX_DAYS ahead; every other entity
 * is capped at the present.
 */
const useDateConstraints = (entity: EntityType) => {
  const maxDate = useMemo(
    () =>
      hasForecast(entity) ? DateTime.now().plus({ days: FORECAST_MAX_DAYS }) : undefined,
    [entity],
  )
  const disableFuture = !hasForecast(entity)

  const getTranslationParams = useCallback(
    (error: DateTimeValidationError) => {
      if ((error === "maxDate" || error === "maxTime") && hasForecast(entity)) {
        return { days: FORECAST_MAX_DAYS }
      }
      return undefined
    },
    [entity],
  )

  return { maxDate, disableFuture, getTranslationParams }
}

/**
 * Whether a vertical scroll box still has content below what it shows. It follows both
 * scrolling and resizing, since the date popover grows when the calendar opens.
 */
export const useCanScrollDown = (): {
  scrollRef: RefObject<HTMLDivElement>
  canScrollDown: boolean
} => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollDown, setCanScrollDown] = useState(false)

  useEffect(() => {
    const element = scrollRef.current
    if (!element || typeof ResizeObserver === "undefined") return
    const update = () =>
      setCanScrollDown(
        element.scrollTop + element.clientHeight < element.scrollHeight - 1,
      )
    const observer = new ResizeObserver(update)
    observer.observe(element)
    if (element.firstElementChild) observer.observe(element.firstElementChild)
    element.addEventListener("scroll", update)
    return () => {
      observer.disconnect()
      element.removeEventListener("scroll", update)
    }
  }, [])

  return { scrollRef, canScrollDown }
}

/** The pickers' locale: British English, for day-first dates and a 24-hour clock. */
export const usePickerLocale = (): string => {
  const { i18n } = useTranslation()
  return i18n.resolvedLanguage === "en" ? "en-gb" : i18n.language
}

export default useDateConstraints
